import { useEffect, useRef } from "react"
import { useHistory, useLocation, useRouteMatch } from "react-router-dom"

/**
 * Manages background location for intercepting route modals.
 *
 * When a route is opened via `history.push` with `state: { background }`,
 * the background location is captured and held in a ref so the originating page
 * keeps rendering behind the modal. Cleans up stale location state on mount.
 *
 * @param interceptPattern - Route pattern that triggers the modal (e.g. `${path}/guests/:guestId`)
 */
const useRouteBackground = (interceptPattern: string) => {
  const history = useHistory()
  const location = useLocation()
  const backgroundRef = useRef<any>(null)
  const routeMatch = useRouteMatch(interceptPattern)

  // Ref must be set during render (not in useEffect) because `background` is
  // read in the same render cycle to decide the Switch location and whether
  // to render the modal. Idempotent — safe with StrictMode.
  if (location.state?.background) {
    backgroundRef.current = location.state.background
  }
  if (!routeMatch) {
    backgroundRef.current = null
  }

  // Clean up stale location.state on mount so a refresh doesn't carry
  // the background forward. Intentionally empty deps — must run once only.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (location.state?.background) {
      history.replace({ ...location, state: undefined })
    }
  }, [])

  // True when the user navigated here from within the app (intercepted route),
  // false when they landed directly (hard reload / new tab / pasted URL).
  const isIntercepted = !!backgroundRef.current

  return {
    background: backgroundRef.current,
    isIntercepted,
    backgroundRef,
    routeMatch,
  }
}

export default useRouteBackground
